import { Button, Input, SegmentedControl } from '@usacow/ds';
import type {
  DrillContent,
  SelectContent,
  TagContent,
  PredictContent,
  FillContent,
} from './content.js';
import type { DrillResponse } from './grade.js';

interface RunnerProps<C extends DrillContent, R extends DrillResponse> {
  content: C;
  value: R;
  onChange: (v: R) => void;
  disabled: boolean;
}

export function SelectRunner({
  content,
  value,
  onChange,
  disabled,
}: RunnerProps<SelectContent, string>) {
  return (
    <div className="dr-opts">
      {content.options.map((o) => (
        <button
          key={o.id}
          className={`dr-opt${value === o.id ? ' is-on' : ''}`}
          disabled={disabled}
          onClick={() => onChange(o.id)}
        >
          <span className="dr-opt__k">{o.id}</span>
          <span className="dr-opt__l">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function TagRunner({
  content,
  value,
  onChange,
  disabled,
}: RunnerProps<TagContent, Record<string, string>>) {
  const options = content.palette.map((t) => ({ value: t, label: t }));
  return (
    <div className="dr-tag">
      {content.spans.map((s) => (
        <div className="dr-tag__row" key={s.id}>
          <span className="dr-tag__text">{s.text}</span>
          <SegmentedControl
            subtle
            value={value[s.id] ?? ''}
            onChange={(t) => !disabled && onChange({ ...value, [s.id]: t })}
            options={options}
          />
        </div>
      ))}
    </div>
  );
}

export function PredictRunner({
  content,
  value,
  onChange,
  disabled,
}: RunnerProps<PredictContent, string>) {
  return (
    <div className="dr-predict">
      {content.context && <div className="dr-predict__ctx mono">{content.context}</div>}
      <Input
        label={content.inputLabel}
        mono
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{ maxWidth: 220 }}
      />
    </div>
  );
}

export function FillRunner({
  content,
  value,
  onChange,
  disabled,
}: RunnerProps<FillContent, string>) {
  return (
    <pre className="dr-fill">
      {content.before}
      <input
        className="dr-fill__in mono"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…"
      />
      {content.after}
    </pre>
  );
}

/** Initial empty response for a given archetype. */
export function emptyResponse(content: DrillContent): DrillResponse {
  return content.archetype === 'tag' ? {} : '';
}

export { Button };
